import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProgressTracker } from './ProgressTracker';
import { SmartSuggestions } from './SmartSuggestions';
import { TutorialProgress } from './TutorialProgress';
import { WorkflowBreadcrumbs } from './WorkflowBreadcrumbs';
import { WorkflowShortcuts } from './WorkflowShortcuts';
import { SystemStatus } from './SystemStatus';
import { AppTabsContent } from './AppTabsContent';
import { APISource, Dataset, ResearchDomainTemplate } from '@/lib/types';
import { beginnerTutorials } from '@/lib/tutorialData';

interface AppMainContentProps {
  activeTab: string;
  selectedDatasets: Dataset[];
  qualityReports: Record<string, any>;
  correlations: any[];
  appliedTemplate: ResearchDomainTemplate | null;
  selectedSource: APISource | null;
  hasVisualizations: boolean;
  hasExports: boolean;
  filteredSources: APISource[];
  allDatasets: Dataset[];
  totalDatasetCount: number;
  isLoading: boolean;
  onTabChange: (tab: string) => void;
  onSuggestionClick: (suggestion: any) => void;
  onShowKeyboardHelp: () => void;
  handleTemplateSelect: (template: ResearchDomainTemplate) => void;
  handleDatasetSelect: (dataset: Dataset) => void;
  handleDatasetPreview: (dataset: Dataset) => void;
  handleSourceSelect: (source: APISource) => void;
  handleFilterChange: (filtered: APISource[]) => void;
  handleAnalyzeCorrelations: () => void;
  handleClearTemplate: () => void;
  handleDataExport: (options: any) => Promise<any>;
  setSelectedDatasets: (datasets: Dataset[]) => void;
  setFilteredSources: (sources: APISource[]) => void;
  setSelectedSource: (source: APISource | null) => void;
}

export function AppMainContent({
  activeTab,
  selectedDatasets,
  qualityReports,
  correlations,
  appliedTemplate,
  selectedSource,
  hasVisualizations,
  hasExports,
  filteredSources,
  allDatasets,
  totalDatasetCount,
  isLoading,
  onTabChange,
  onSuggestionClick,
  onShowKeyboardHelp,
  handleTemplateSelect,
  handleDatasetSelect,
  handleDatasetPreview,
  handleSourceSelect,
  handleFilterChange,
  handleAnalyzeCorrelations,
  handleClearTemplate,
  handleDataExport,
  setSelectedDatasets,
  setFilteredSources,
  setSelectedSource,
}: AppMainContentProps) {
  return (
    <main className='container mx-auto px-4 sm:px-6 py-6 max-w-full overflow-x-hidden'>
      <div className='space-y-6 max-w-full'>
        {/* Progress Tracker */}
        <ProgressTracker
          selectedDatasetCount={selectedDatasets.length}
          hasQualityReports={Object.keys(qualityReports).length > 0}
          hasCorrelations={correlations.length > 0}
          hasVisualizations={hasVisualizations}
          hasExports={hasExports}
          currentTab={activeTab}
          onStepClick={onTabChange}
        />

        {/* Smart Suggestions */}
        <SmartSuggestions
          selectedDatasetCount={selectedDatasets.length}
          hasQualityReports={Object.keys(qualityReports).length > 0}
          hasCorrelations={correlations.length > 0}
          currentTab={activeTab}
          onSuggestionClick={onSuggestionClick}
        />

        <TutorialProgress totalTutorials={beginnerTutorials.length} />

        {/* Breadcrumb Navigation */}
        <WorkflowBreadcrumbs
          activeTab={activeTab}
          onTabChange={onTabChange}
          selectedDatasetCount={selectedDatasets.length}
          appliedTemplate={appliedTemplate}
          selectedSource={selectedSource}
        />

        {/* Enhanced Workflow Navigation with Shortcuts */}
        <WorkflowShortcuts
          activeTab={activeTab}
          onTabChange={onTabChange}
          selectedDatasetCount={selectedDatasets.length}
          hasQualityReports={Object.keys(qualityReports).length > 0}
          hasCorrelations={correlations.length > 0}
          onShowKeyboardHelp={onShowKeyboardHelp}
        />

        {/* System Status - Hidden on small screens */}
        <div className='hidden lg:block'>
          <SystemStatus />
        </div>

        <Tabs value={activeTab} onValueChange={onTabChange}>
          {/* Hidden TabsList for functionality, but visually replaced by workflow */}
          <TabsList className='hidden'>
            <TabsTrigger value='templates'>Templates</TabsTrigger>
            <TabsTrigger value='discover'>Discover</TabsTrigger>
            <TabsTrigger value='datasets'>Datasets</TabsTrigger>
            <TabsTrigger value='browse-all'>Browse All</TabsTrigger>
            <TabsTrigger value='quality'>Quality</TabsTrigger>
            <TabsTrigger value='correlations'>Correlations</TabsTrigger>
            <TabsTrigger value='transform'>Transform</TabsTrigger>
            <TabsTrigger value='visualize'>Visualize</TabsTrigger>
            <TabsTrigger value='export'>Export</TabsTrigger>
          </TabsList>

          <AppTabsContent
            selectedDatasets={selectedDatasets}
            activeTab={activeTab}
            selectedSource={selectedSource}
            qualityReports={qualityReports}
            correlations={correlations}
            filteredSources={filteredSources}
            appliedTemplate={appliedTemplate}
            allDatasets={allDatasets}
            totalDatasetCount={totalDatasetCount}
            isLoading={isLoading}
            onTabChange={onTabChange}
            handleTemplateSelect={handleTemplateSelect}
            handleDatasetSelect={handleDatasetSelect}
            handleDatasetPreview={handleDatasetPreview}
            handleSourceSelect={handleSourceSelect}
            handleFilterChange={handleFilterChange}
            handleAnalyzeCorrelations={handleAnalyzeCorrelations}
            handleClearTemplate={handleClearTemplate}
            handleDataExport={handleDataExport}
            setSelectedDatasets={setSelectedDatasets}
            setFilteredSources={setFilteredSources}
            setSelectedSource={setSelectedSource}
          />
        </Tabs>
      </div>
    </main>
  );
}
